import { generatePath, matchPath } from "react-router";

/**
 * App route patterns in react-router format (`:id` placeholders).
 * Use directly for static paths and `<Route path>` definitions; pass the
 * `:param` ones through `generatePath(pattern, params)` to navigate.
 */
export const routes = {
  home: "/",
  tests: {
    root: "/tests",
    new: "/tests/new",
    bulkInput: "/tests/wc/:standard/bulk-input",
    detail: "/tests/:id",
  },
  processingTests: {
    root: "/processing-tests",
    batch: "/processing-tests/batch",
    detail: "/processing-tests/:id",
  },
  accessManagement: "/accessmanagement",
  ags: {
    root: "/ags",
    project: "/ags/:projectId",
  },
  reports: {
    root: "/reports",
    project: "/reports/:projectId",
    create: "/reports/:projectId/create",
    container: "/reports/:projectId/containers/:containerId",
  },
} as const;

export const reportsProjectPath = (projectId: string): string => generatePath(routes.reports.project, { projectId });

export const reportsCreatePath = (projectId: string): string => generatePath(routes.reports.create, { projectId });

export const reportsContainerPath = (projectId: string, containerId: string): string =>
  generatePath(routes.reports.container, { projectId, containerId });

/** Every leaf string value in `routes`, recursively inferred from the `as const` definition. */
type RoutePathOf<T> = T extends string ? T : { [K in keyof T]: RoutePathOf<T[K]> }[keyof T];

export type RoutePath = RoutePathOf<typeof routes>;

export interface RouteConfig {
  /** Render the page flush against the app shell, without the default root padding. */
  noPadding?: boolean;
}

/** Per-route configuration, keyed by a route pattern; read it through `getRouteConfig`. */
export const routeConfig: Partial<Record<RoutePath, RouteConfig>> = {
  [routes.tests.bulkInput]: { noPadding: true },
};

export const getRouteConfig = (pathname: string): RouteConfig | undefined =>
  Object.entries(routeConfig).find(([pattern]) => matchPath(pattern, pathname))?.[1];
